import React, { useEffect, useRef, useState } from "react";
import {
  FaSave,
  FaTimes,
  FaCloudUploadAlt,
  FaLink,
  FaImage,
  FaCropAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";

import UserDrawer from "./UserDrawer";

import { updateUser, getUserById } from "../../../../../services/userServices";

import {
  uploadUserPhoto,
  cropUserPhoto,
  toAbsoluteUserPhotoUrl,
} from "../../../../../services/userPhotoServices";

import CropPhotoModal, {
  toMasterUrl,
} from "../../../../../components/CropPhotoModal/CropPhotoModal";

const DEFAULT_AVATAR = "/img/user_avatar.jpg";

function withBust(url, version) {
  if (!url) return url;
  return url.includes("?") ? `${url}&v=${version}` : `${url}?v=${version}`;
}

export default function EditUserDrawer({
  isOpen,
  onClose,
  refreshList,
  userId,
}) {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    role_id: "",
    photoURL: "",
  });

  const [saving, setSaving] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);

  const [imgOk, setImgOk] = useState(true);
  const [uploadMode, setUploadMode] = useState("url");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [imgVersion, setImgVersion] = useState(0);
  const [isCropOpen, setIsCropOpen] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !userId) return;

    let mounted = true;

    const fetchUser = async () => {
      try {
        setLoadingUser(true);

        const data = await getUserById(userId);

        if (!mounted) return;

        setFormData({
          nom: data?.nom || "",
          prenom: data?.prenom || "",
          email: data?.email || "",
          role_id: data?.role_id ?? "",
          photoURL: data?.photoURL || "",
        });

        setImgOk(true);
        setUploadMode("url");
        setUploadProgress(0);
        setIsUploading(false);
        setImgVersion((value) => value + 1);
      } catch (error) {
        console.error("Erreur chargement utilisateur :", error);
        toast.error("Erreur lors du chargement de l'utilisateur.");
      } finally {
        if (mounted) setLoadingUser(false);
      }
    };

    fetchUser();

    return () => {
      mounted = false;
    };
  }, [isOpen, userId]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "role_id" ? Number(value || 0) : value,
    }));

    if (name === "photoURL") {
      setImgOk(true);
      setImgVersion((version) => version + 1);
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!userId) {
      toast.error("ID utilisateur manquant.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const response = await uploadUserPhoto(userId, file, {
        format: outputFormat,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percent);
        },
      });

      const newPhotoUrl =
        response?.photo_url ||
        response?.photoURL ||
        response?.imageUrl ||
        response?.url ||
        response?.path ||
        response?.data?.photo_url ||
        response?.data?.photoURL ||
        response?.data?.imageUrl ||
        response?.data?.url ||
        response?.data?.path;

      if (newPhotoUrl) {
        setFormData((prev) => ({
          ...prev,
          photoURL: newPhotoUrl,
        }));

        setImgOk(true);
        setImgVersion((version) => version + 1);
        toast.success("Photo uploadée avec succès.");
      } else {
        console.warn("Réponse upload inattendue :", response);
        toast.warn("Upload terminé, mais aucune URL n'a été renvoyée.");
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Erreur upload photo utilisateur :", error);
      toast.error(`Erreur upload : ${error?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isUploading) {
      toast.info("Patiente, l'upload de la photo est encore en cours.");
      return;
    }

    try {
      setSaving(true);

      await updateUser(userId, { ...formData });

      toast.success("Utilisateur mis à jour avec succès.");
      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur mise à jour utilisateur :", error);
      toast.error("Erreur lors de la mise à jour de l'utilisateur.");
    } finally {
      setSaving(false);
    }
  };

  const absolutePhoto = toAbsoluteUserPhotoUrl(formData.photoURL);
  const previewSrc = formData.photoURL
    ? withBust(absolutePhoto, imgVersion)
    : withBust(DEFAULT_AVATAR, imgVersion);

  return (
    <>
      <UserDrawer
        isOpen={isOpen}
        onClose={onClose}
        title="Modifier un utilisateur"
        subtitle="Mettez à jour l’identité, le rôle et la photo du compte."
        width="max-w-4xl"
      >
        <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
          <div className="flex-1 space-y-5 p-4 sm:p-6">
            {loadingUser ? (
              <div className="space-y-4">
                <div className="h-24 animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
                <div className="h-64 animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
              </div>
            ) : (
              <>
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                      <FaImage />
                    </span>

                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">
                        Identité & avatar
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Les informations modifiées seront visibles sur le compte
                        utilisateur.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                          Nom
                        </label>
                        <input
                          type="text"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          required
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                          Prénom
                        </label>
                        <input
                          type="text"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleChange}
                          required
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                          Rôle
                        </label>
                        <select
                          name="role_id"
                          value={formData.role_id}
                          onChange={handleChange}
                          required
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                        >
                          <option value="1">Administrateur</option>
                          <option value="2">Utilisateur</option>
                          <option value="3">Enseignant</option>
                        </select>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                          URL photo
                        </label>

                        <div className="mb-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setUploadMode("url")}
                            className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-bold transition ${
                              uploadMode === "url"
                                ? "bg-indigo-600 text-white"
                                : "border border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                            }`}
                          >
                            <FaLink />
                            URL
                          </button>

                          <button
                            type="button"
                            onClick={() => setUploadMode("upload")}
                            className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-bold transition ${
                              uploadMode === "upload"
                                ? "bg-indigo-600 text-white"
                                : "border border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                            }`}
                          >
                            <FaCloudUploadAlt />
                            Upload
                          </button>
                        </div>

                        {uploadMode === "url" ? (
                          <input
                            type="text"
                            name="photoURL"
                            value={formData.photoURL}
                            onChange={handleChange}
                            placeholder="https://... ou /uploads/..."
                            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                          />
                        ) : (
                          <div className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-950">
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleFileChange}
                              className="hidden"
                            />

                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={openFilePicker}
                                disabled={isUploading}
                                className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:opacity-60"
                              >
                                <FaCloudUploadAlt />
                                Choisir un fichier
                              </button>

                              <select
                                value={outputFormat}
                                onChange={(event) =>
                                  setOutputFormat(event.target.value)
                                }
                                disabled={isUploading}
                                className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                              >
                                <option value="webp">WEBP</option>
                                <option value="jpg">JPG</option>
                                <option value="png">PNG</option>
                              </select>
                            </div>

                            {isUploading ? (
                              <div className="mt-3">
                                <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                                  <div
                                    className="h-full rounded-full bg-indigo-600"
                                    style={{ width: `${uploadProgress}%` }}
                                  />
                                </div>
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                  Upload : {uploadProgress}%
                                </p>
                              </div>
                            ) : null}

                            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                              L’image sera optimisée côté serveur.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">
                        Aperçu photo
                      </p>

                      <div className="mt-3 aspect-square overflow-hidden rounded-[24px] border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-900">
                        <img
                          key={imgVersion}
                          src={previewSrc}
                          alt="Aperçu utilisateur"
                          onError={() => setImgOk(false)}
                          className="h-full w-full object-cover"
                        />
                      </div>

                      {!imgOk ? (
                        <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                          Impossible de charger l’image.
                        </p>
                      ) : null}

                      {formData.photoURL ? (
                        <button
                          type="button"
                          onClick={() => setIsCropOpen(true)}
                          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <FaCropAlt />
                          Recadrer / Zoom
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:px-6">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="submit"
              disabled={saving || isUploading || loadingUser}
              className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Mettre à jour"}
            </button>
          </div>
        </form>
      </UserDrawer>

      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={userId}
        imageUrl={
          formData.photoURL
            ? `${toAbsoluteUserPhotoUrl(toMasterUrl(formData.photoURL))}?v=${imgVersion}`
            : ""
        }
        onCropped={(newUrl) => {
          setFormData((prev) => ({
            ...prev,
            photoURL: newUrl,
          }));
          setImgVersion((version) => version + 1);
        }}
        format="webp"
        cropAction={cropUserPhoto}
      />
    </>
  );
}